import React from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { TableCell, TableRow } from '@/components/ui/Table';
import { Button, UnstyledButton } from '@/components/ui/Buttons';
import Icon from '@/components/ui/Icon';
import Text from '@/components/ui/Text';
import I18n from '@/modules/i18n';
import cn from '@/utils/cn';
import stripStandardsAliasPrefix from '@/utils/stripStandardsAliasPrefix';
import { correlationMutationKey } from '../hooks/useCorrelationActions';
import COLUMN_WIDTHS from './columns';
import SourceBadge from './SourceBadge';
import { correlationLabel, ModelCorrelation } from '../types';

interface CorrelationRowProps {
  correlation: ModelCorrelation;
  isMutatingBatch: boolean;
  isSelected: boolean;
  modelId: number;
  modelType: string;
  onRemove: (correlation: ModelCorrelation, slot: number) => void;
  onToggleSelect: (id: number) => void;
  onUnhide: (correlation: ModelCorrelation, slot: number) => void;
  slot: number;
}

const ROW_CELL = 'tw-py-2 tw-align-top';

const CorrelationRow = ({
  correlation, isMutatingBatch, isSelected, modelId, modelType, onRemove, onToggleSelect, onUnhide, slot,
}: CorrelationRowProps) => {
  const { description, hidden, id, source, source_label: sourceLabel, standard } = correlation;
  const label = correlationLabel(correlation);
  const descriptionText = stripStandardsAliasPrefix(description, standard);

  const isMutating = useIsMutating({
    mutationKey: correlationMutationKey(modelType, modelId),
    predicate: mutation => (mutation.state.variables as ModelCorrelation | undefined)?.id === id,
  }) > 0;

  return (
    <TableRow
      aria-label={hidden ? I18n.t('hidden_correlation_row', { standard: label }) : undefined}
      className={cn(
        'tw-cursor-pointer tw-border-b tw-border-solid tw-border-light-grey',
        'hover:tw-bg-light-grey focus-within:tw-bg-light-grey focus-within:tw-outline focus-within:tw-outline-2',
        'focus-within:tw-outline-offset-[-2px] focus-within:tw-outline-tci-purple',
        hidden && 'tw-bg-light-grey',
      )}
    >
      <TableCell className={cn(COLUMN_WIDTHS.select, ROW_CELL, 'tw-px-2 tw-text-center')}>
        {!hidden && (
          <input
            aria-label={I18n.t('select_correlation', { standard: label })}
            checked={isSelected}
            type="checkbox"
            onChange={() => onToggleSelect(id)}
          />
        )}
      </TableCell>
      <TableCell className={cn(COLUMN_WIDTHS.standard, ROW_CELL)}>
        <span className="tw-inline-flex tw-items-baseline tw-gap-1.5 tw-max-w-full">
          <Text className="tw-text-tci-black tw-break-words" size="sm" weight="semibold">{standard}</Text>
          {hidden && <Icon aria-hidden className="tw-shrink-0 tw-text-dark-grey" icon="Hide" size={14} />}
        </span>
      </TableCell>
      <TableCell className={cn(COLUMN_WIDTHS.description, ROW_CELL)}>
        <Text className={hidden ? 'tw-text-dark-grey' : 'tw-text-very-dark-grey'} size="sm">
          {descriptionText}
        </Text>
      </TableCell>
      <TableCell className={cn(COLUMN_WIDTHS.source, ROW_CELL)}>
        <SourceBadge label={sourceLabel} source={source} />
      </TableCell>
      <TableCell className={cn(COLUMN_WIDTHS.remove, ROW_CELL, 'tw-px-0 tw-text-center')}>
        {hidden ? (
          <Button
            outlineStyle
            small
            aria-label={I18n.t('unhide_correlation', { standard: label })}
            className="tw-h-6 tw-whitespace-nowrap"
            data-row-action={id}
            disabled={isMutating || isMutatingBatch}
            onClick={() => onUnhide(correlation, slot)}
          >
            {I18n.t('unhide')}
          </Button>
        ) : (
          <UnstyledButton
            aria-label={I18n.t('remove_correlation', { standard: label })}
            className="tw-inline-flex tw-items-center tw-justify-center tw-align-middle tw-text-dark-grey"
            data-row-action={id}
            disabled={isMutating || isMutatingBatch}
            onClick={() => onRemove(correlation, slot)}
          >
            <Icon aria-hidden icon="Close" size={16} />
          </UnstyledButton>
        )}
      </TableCell>
    </TableRow>
  );
};

// Memoized because search re-renders the table on every keystroke and "Show more" can put
// thousands of these on screen. Only effective while the callbacks stay stable.
export default React.memo(CorrelationRow);
